import { studySummaryPresentation } from '../hooks/study-builder/studyAuthoringPresentation'
import { useCallback, useEffect, useState } from 'react'
import type { ShopifyAuthoredTreatmentType } from '@testfeed/shopify-contracts'
import type { StudySetupSort } from './study-setup-index'
import type { SavedStudyBuilderDraftSummary } from '../hooks/study-builder/studyBuilderDrafts'
import type { StudySetupMerchantQueue, StudySetupStatusKey } from './study-setup-lifecycle'
import { buildStudySetupsPageControls } from './study-setups-page-controls'
import type { StudySetupIndexRow, StudySetupPageState } from './study-setups-page-controller-types'
import { studySetupRowId } from './study-deletion-eligibility'
import {
  MERCHANT_QUEUE_FILTERS,
  STUDY_AUDIENCE_QUERY_KEY,
  STUDY_PRODUCT_QUERY_KEY,
  STUDY_QUERY_KEY,
  STUDY_QUEUE_QUERY_KEY,
  STUDY_SORT_QUERY_KEY,
  STUDY_TREATMENT_QUERY_KEY,
  STUDY_PAGE_QUERY_KEY,
  listFromUrl,
  pageFromUrl,
  queueFromUrl,
  selectedSortValue,
  setListInUrl,
  statusKeysFromUrl,
  STUDY_LIFECYCLE_QUERY_KEY,
  valueFromUrl,
} from './study-setups-page-state'

type ControllerProps = {
  savedDrafts?: SavedStudyBuilderDraftSummary[]
  onSelectionContextChange?: () => void
}

export function useStudySetupsPageController({ savedDrafts = [], onSelectionContextChange }: ControllerProps) {
  const [selectedQueue, setSelectedQueue] = useState<StudySetupMerchantQueue>(queueFromUrl)
  const [queryValue, setQueryValue] = useState(() => valueFromUrl(STUDY_QUERY_KEY))
  const [sort, setSort] = useState<StudySetupSort>(() => selectedSortValue([valueFromUrl(STUDY_SORT_QUERY_KEY)]))
  const [selectedAudienceValues, setSelectedAudienceValues] = useState<string[]>(() =>
    listFromUrl(STUDY_AUDIENCE_QUERY_KEY),
  )
  const [selectedTreatmentTypes, setSelectedTreatmentTypes] = useState<ShopifyAuthoredTreatmentType[]>(
    () => listFromUrl(STUDY_TREATMENT_QUERY_KEY) as ShopifyAuthoredTreatmentType[],
  )
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>(() => listFromUrl(STUDY_PRODUCT_QUERY_KEY))
  const [selectedLifecycleValues, setSelectedLifecycleValues] = useState<StudySetupStatusKey[]>(statusKeysFromUrl)
  const [page, setPage] = useState(() => pageFromUrl(globalThis.window?.location.search ?? ''))

  const syncUrl = useCallback((mutate: (url: URL) => void) => {
    if (typeof window === 'undefined') return
    const url = new URL(globalThis.window.location.href)
    mutate(url)
    globalThis.window.history.pushState(null, '', `${url.pathname}${url.search}${url.hash}`)
  }, [])

  const commitState = (changes: StudySetupPageState) => {
    onSelectionContextChange?.()
    const nextQueue = changes.queue ?? selectedQueue
    const nextQuery = changes.query ?? queryValue
    const nextSort = changes.sort ?? sort
    const nextAudience = changes.audience ?? selectedAudienceValues
    const nextTreatment = changes.treatment ?? selectedTreatmentTypes
    const nextProduct = changes.product ?? selectedProductIds
    const nextLifecycle = changes.lifecycle ?? selectedLifecycleValues
    const nextPage = changes.page ?? page
    setSelectedQueue(nextQueue)
    setQueryValue(nextQuery)
    setSort(nextSort)
    setSelectedAudienceValues(nextAudience)
    setSelectedTreatmentTypes(nextTreatment as ShopifyAuthoredTreatmentType[])
    setSelectedProductIds(nextProduct)
    setSelectedLifecycleValues(nextLifecycle)
    setPage(nextPage)
    syncUrl((url) => {
      if (nextQueue === 'all') url.searchParams.delete(STUDY_QUEUE_QUERY_KEY)
      else url.searchParams.set(STUDY_QUEUE_QUERY_KEY, nextQueue)
      if (nextQuery) url.searchParams.set(STUDY_QUERY_KEY, nextQuery)
      else url.searchParams.delete(STUDY_QUERY_KEY)
      url.searchParams.set(STUDY_SORT_QUERY_KEY, nextSort.replace(/-(asc|desc)$/, ' $1'))
      setListInUrl(url, STUDY_AUDIENCE_QUERY_KEY, nextAudience)
      setListInUrl(url, STUDY_TREATMENT_QUERY_KEY, nextTreatment)
      setListInUrl(url, STUDY_PRODUCT_QUERY_KEY, nextProduct)
      setListInUrl(url, STUDY_LIFECYCLE_QUERY_KEY, nextLifecycle)
      if (nextPage > 1) url.searchParams.set(STUDY_PAGE_QUERY_KEY, String(nextPage))
      else url.searchParams.delete(STUDY_PAGE_QUERY_KEY)
    })
  }

  useEffect(() => {
    const onPopState = () => {
      onSelectionContextChange?.()
      setSelectedQueue(queueFromUrl())
      setQueryValue(valueFromUrl(STUDY_QUERY_KEY))
      setSort(selectedSortValue([valueFromUrl(STUDY_SORT_QUERY_KEY)]))
      setSelectedAudienceValues(listFromUrl(STUDY_AUDIENCE_QUERY_KEY))
      setSelectedTreatmentTypes(listFromUrl(STUDY_TREATMENT_QUERY_KEY) as ShopifyAuthoredTreatmentType[])
      setSelectedProductIds(listFromUrl(STUDY_PRODUCT_QUERY_KEY))
      setSelectedLifecycleValues(statusKeysFromUrl())
      setPage(pageFromUrl(globalThis.window?.location.search ?? ''))
    }
    globalThis.window?.addEventListener('popstate', onPopState)
    return () => globalThis.window?.removeEventListener('popstate', onPopState)
  }, [onSelectionContextChange])

  const normalizedQuery = queryValue.trim().toLocaleLowerCase()
  const matchingSavedDrafts = savedDrafts.filter((draft) => {
    if (
      (selectedAudienceValues.length > 0 && !selectedAudienceValues.includes(draft.audience?.value ?? '')) ||
      (selectedTreatmentTypes.length > 0 &&
        (!draft.treatmentType || !selectedTreatmentTypes.includes(draft.treatmentType))) ||
      (selectedProductIds.length > 0 && !selectedProductIds.includes(draft.productId ?? ''))
    )
      return false
    if (selectedLifecycleValues.length > 0 && !selectedLifecycleValues.includes(studySummaryPresentation(draft).key))
      return false
    if (!normalizedQuery) return true
    return (
      draft.title.toLocaleLowerCase().includes(normalizedQuery) ||
      (draft.productTitle?.toLocaleLowerCase().includes(normalizedQuery) ?? false)
    )
  })
  const queueCounts = MERCHANT_QUEUE_FILTERS.reduce<Record<StudySetupMerchantQueue, number>>(
    (counts, filter) => {
      counts[filter.id] =
        filter.id === 'all'
          ? matchingSavedDrafts.length
          : matchingSavedDrafts.filter((draft) => studySummaryPresentation(draft).queue === filter.id).length
      return counts
    },
    { all: 0, drafts: 0, running: 0, 'results-ready': 0, errors: 0 },
  )
  const views = MERCHANT_QUEUE_FILTERS.map((filter) => ({
    id: filter.id,
    label: `${filter.label} (${queueCounts[filter.id]})`,
  }))
  const visibleSavedDrafts = matchingSavedDrafts.filter(
    (draft) => selectedQueue === 'all' || studySummaryPresentation(draft).queue === selectedQueue,
  )
  const mixedRows: StudySetupIndexRow[] = visibleSavedDrafts
    .map((draft) => ({
      kind: 'builder-draft' as const,
      id: studySetupRowId(draft.draftId),
      draft,
    }))
    .toSorted((left, right) => {
      const leftTitle = left.draft.title
      const rightTitle = right.draft.title
      const leftProduct = left.draft.productTitle ?? ''
      const rightProduct = right.draft.productTitle ?? ''
      const leftDate = left.draft.createdAt ?? left.draft.updatedAt
      const rightDate = right.draft.createdAt ?? right.draft.updatedAt
      const leftStatus = studySummaryPresentation(left.draft).label
      const rightStatus = studySummaryPresentation(right.draft).label
      let comparison = 0
      if (sort === 'product-title-asc' || sort === 'product-title-desc')
        comparison = leftProduct.localeCompare(rightProduct)
      else if (sort === 'study-title-asc' || sort === 'study-title-desc')
        comparison = leftTitle.localeCompare(rightTitle)
      else if (sort === 'status-asc') comparison = leftStatus.localeCompare(rightStatus)
      else if (sort === 'treatment-count-asc' || sort === 'treatment-count-desc') {
        const leftCount = left.draft.versionCount ?? null
        const rightCount = right.draft.versionCount ?? null
        if (leftCount === null || rightCount === null)
          return leftCount === rightCount ? left.id.localeCompare(right.id) : leftCount === null ? 1 : -1
        comparison = leftCount - rightCount
      } else comparison = (Date.parse(leftDate) || 0) - (Date.parse(rightDate) || 0)
      const descending = sort.endsWith('-desc')
      return (descending ? -comparison : comparison) || left.id.localeCompare(right.id)
    })
  const pageCount = Math.max(1, Math.ceil(mixedRows.length / 25))
  const visiblePage = Math.min(page, pageCount)
  const pagedRows = mixedRows.slice((visiblePage - 1) * 25, visiblePage * 25)
  const { filters: studyFilters, appliedFilters } = buildStudySetupsPageControls(
    savedDrafts,
    selectedProductIds,
    selectedAudienceValues,
    selectedTreatmentTypes,
    selectedLifecycleValues,
    commitState,
  )

  return {
    selectedQueue,
    queryValue,
    sort,
    studyFilters,
    appliedFilters,
    views,
    pagedRows,
    visiblePage,
    pageCount,
    commitState,
    selectSort: (value: string[]) => commitState({ sort: selectedSortValue(value), page: 1 }),
    clearFilters: () =>
      commitState({
        queue: 'all',
        query: '',
        audience: [],
        treatment: [],
        product: [],
        lifecycle: [],
        page: 1,
      }),
  }
}
